import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, ChevronRight, ChevronDown, HelpCircle } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import SideMenu from './SideMenu';
import CookieConsent from './CookieConsent';
import StickyBreadcrumb from './StickyBreadcrumb';
import PrivacyPolicy from './PrivacyPolicy';

const SITE = 'https://fernandotech.com.br';
const DEFAULT_TITLE = 'Fernando Tech – Suporte Técnico Premium - Ribeirão Preto e Região';
const WHATSAPP = 'https://wa.me/5516991378312?text=Ol%C3%A1!%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es.';

/**
 * Padrão único para páginas dedicadas por assunto (Fase 3).
 * Gerencia SEO (title, description, canonical, JSON-LD) e a moldura
 * (Navbar, breadcrumb, hero, conteúdo reaproveitado, CTA, footer).
 */
const SubjectPageLayout = ({
  path,
  breadcrumbName,
  breadcrumbIcon,
  seoTitle,
  seoDescription,
  extraSchema = null,
  faq = null,
  hero = {},
  cta = {},
  children,
}) => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [visible, setVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const url = `${SITE}${path}`;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = seoTitle;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    const prevDesc = meta.content;
    meta.content = seoDescription;

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = url;

    const graph = [
      { "@type": "WebPage", "@id": url, "url": url, "name": seoTitle, "description": seoDescription, "inLanguage": "pt-BR", "isPartOf": { "@id": `${SITE}/#website` } },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/` },
          { "@type": "ListItem", "position": 2, "name": breadcrumbName, "item": url },
        ],
      },
    ];
    if (extraSchema) graph.push(extraSchema);
    if (faq && faq.length) {
      graph.push({
        "@type": "FAQPage",
        "mainEntity": faq.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      });
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-subject-page';
    script.text = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    const t = setTimeout(() => setVisible(true), 80);

    return () => {
      clearTimeout(t);
      document.title = DEFAULT_TITLE;
      if (meta) meta.content = prevDesc;
      if (canonical) canonical.href = `${SITE}/`;
      const ld = document.getElementById('ld-subject-page');
      if (ld) ld.remove();
    };
  }, [path, seoTitle, seoDescription, breadcrumbName, url, extraSchema, faq]);

  const primary = cta.primary || { label: 'Falar no WhatsApp', href: WHATSAPP };
  const secondary = cta.secondary || { label: 'Ver todos os serviços', to: '/' };

  return (
    <div className="bg-slate-950 min-h-screen">
      <Navbar />

      <StickyBreadcrumb>
        <nav className="flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center gap-1 hover:text-cyan-400 transition-colors">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
          <span className="flex items-center gap-1 text-cyan-400">
            {breadcrumbIcon} {breadcrumbName}
          </span>
        </nav>
      </StickyBreadcrumb>

      {/* Hero padrão */}
      <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        </div>
        <div
          className="container mx-auto px-4 text-center relative z-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)', transition: 'opacity .5s ease, transform .5s ease' }}
        >
          {hero.badge && (
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold mb-5">
              {hero.badgeIcon} {hero.badge}
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight">
            {hero.title}
            {hero.accent && (
              <><br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{hero.accent}</span></>
            )}
          </h1>
          {hero.subtitle && (
            <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">{hero.subtitle}</p>
          )}
        </div>
      </section>

      {/* Conteúdo reaproveitado da home */}
      {children}

      {/* FAQ com dados estruturados (FAQPage) */}
      {faq && faq.length > 0 && (
        <section className="py-16 bg-slate-950 border-t border-slate-800/60" data-testid="faq-section" aria-label="Perguntas frequentes">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold mb-4">
                <HelpCircle className="w-4 h-4" /> Perguntas Frequentes
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Dúvidas sobre <span className="text-cyan-400">{breadcrumbName}</span>
              </h2>
            </div>
            <div className="space-y-3">
              {faq.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all ${open ? 'bg-slate-900 border-cyan-500/40' : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}`}
                    data-testid={`faq-item-${i}`}
                  >
                    <button
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                      data-testid={`faq-question-${i}`}
                      aria-expanded={open}
                    >
                      <span className="text-white font-semibold text-sm sm:text-base">{f.q}</span>
                      <ChevronDown className={`w-5 h-5 flex-shrink-0 text-cyan-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                    </button>
                    <div
                      className="grid transition-all duration-300 ease-in-out"
                      style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
                    >
                      <div className="overflow-hidden">
                        <p className="px-5 pb-5 text-slate-400 text-sm leading-relaxed">{f.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA final padrão */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-blue-950/30 to-slate-900 border-t border-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 bg-cyan-500/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10 max-w-2xl">
          <h2 className="text-white font-extrabold text-2xl sm:text-3xl mb-3 leading-tight">
            {cta.heading || 'Precisa de ajuda especializada?'}
          </h2>
          <p className="text-slate-400 text-sm mb-8 leading-relaxed">
            {cta.text || 'Fale com a Fernando Tech e receba atendimento rápido em Ribeirão Preto e região. Diagnóstico e orçamento sem compromisso.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={primary.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full shadow-lg shadow-emerald-500/20 transition-all hover:scale-105 text-sm"
              data-testid="subject-cta-primary"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/></svg>
              {primary.label}
            </a>
            {secondary && (
              <Link
                to={secondary.to || '/'}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-slate-500 text-white font-bold rounded-full transition-all hover:scale-105 text-sm"
                data-testid="subject-cta-secondary"
              >
                {secondary.label}
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer onOpenPrivacyPolicy={() => setShowPrivacy(true)} />
      <SideMenu />
      <CookieConsent onOpenPrivacyPolicy={() => setShowPrivacy(true)} />
      <PrivacyPolicy isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
};

export default SubjectPageLayout;
